/**
 * HTTP/SSE AI chat provider implementation.
 *
 * Sends messages to the backend API and parses SSE streams
 * to deliver real-time AI responses.
 *
 * @module
 */
import { t } from '@molecule/app-i18n';
/**
 * HTTP/SSE-based implementation of `ChatProvider`. Sends messages via POST to a backend
 * endpoint and reads SSE (Server-Sent Events) streams for real-time AI responses.
 */
export class HttpChatProvider {
    name = 'http';
    config;
    abortController = null;
    /**
     * Whether the server reported an active stream for the conversation
     * on the last `loadHistory` call. Used by the client to decide whether
     * to poll for updates after a page refresh.
     */
    isServerStreaming = false;
    /**
     * Extra top-level fields the server returned on the last `loadHistory` GET,
     * beyond `messages` and `streaming` — carried through verbatim so this shared
     * bond stays agnostic to any consuming app's vocabulary (Anti-Pattern 14).
     *
     * The bond names NO app-specific concept here. A consuming app reads whatever
     * it persisted from this bag, e.g. molecule.dev restores its agent mode via
     * `provider.lastMeta?.mode` (the value its server puts on the GET response).
     */
    lastMeta = {};
    /** Maximum number of retries for HTTP 409 (server-side lock not yet released). */
    static CONFLICT_MAX_RETRIES = 10;
    /** Base delay in ms between 409 retries (doubled each attempt). */
    static CONFLICT_BASE_DELAY = 500;
    /**
     * The last SSE `id:` seen on this provider's stream. Sent as `Last-Event-ID`
     * on a resume so a server that supports resumption can continue from the
     * event after it instead of replaying the whole turn.
     */
    lastEventId = null;
    /**
     * Statuses treated as transient while RE-ATTACHING to a running turn. A
     * fresh send is never retried: the POST may create a turn, and repeating it
     * without a server-side idempotency key could start two.
     */
    static RESUME_RETRY_STATUSES = [502, 503, 504];
    /** Maximum retries when re-attaching during a drain. */
    static RESUME_MAX_RETRIES = 2;
    constructor(config = {}) {
        this.config = config;
    }
    /**
     * The last SSE event id seen on this provider's stream, or `null`.
     *
     * A caller that reconnects after a drop passes it as `lastEventId` on the
     * resume so the server can continue from the next event.
     *
     * @returns The last event id.
     */
    getLastEventId() {
        return this.lastEventId;
    }
    /**
     * Sends a chat message to the backend and streams back SSE events.
     * @param message - The user message text.
     * @param config - Chat configuration including the API endpoint and model.
     * @param onEvent - Callback invoked for each SSE event (content chunks, errors, done signals).
     * @param attachments - Optional file attachments (images, PDFs, audio, video) as base64.
     */
    async sendMessage(message, config, onEvent, attachments) {
        // Abort any previous in-flight request before starting a new one.
        // Prevents stale streaming events from corrupting state when called concurrently
        // (e.g., React StrictMode double-mount or rapid message sends).
        this.abortController?.abort();
        const controller = new AbortController();
        this.abortController = controller;
        const { signal } = controller;
        const url = `${this.config.baseUrl ?? ''}${config.endpoint}`;
        // Single try-catch wraps everything including the initial fetch so that
        // AbortErrors thrown at any stage (fetch, response.text(), reader.read())
        // are all handled uniformly rather than propagating to the caller.
        try {
            // Retry loop for HTTP 409 — the server holds a per-conversation lock
            // that may not be released yet after a page refresh or rapid resend.
            let response;
            // Body of a 409, read exactly once per attempt (a body can be consumed only
            // once, so the error path below reuses this instead of reading it again).
            let conflictText;
            for (let attempt = 0; attempt <= HttpChatProvider.CONFLICT_MAX_RETRIES; attempt++) {
                response = await HttpChatProvider.fetchResumeAware(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        // A resume re-POSTs a turn that may still be RUNNING server-side —
                        // asking for the event stream lets the server attach this request to
                        // that live turn (the `attached` frame) instead of 409ing. New sends
                        // must NOT ask: attaching never reads the body, so a new message has
                        // to keep the 409-retry path below to run after the current turn.
                        ...(config.resume ? { Accept: 'text/event-stream' } : {}),
                        // Continue from the last event this client received. A server that
                        // honors Last-Event-ID replays only what was missed — the standard
                        // SSE resume contract during a drain/restart.
                        ...((config.resume ? (config.lastEventId ?? this.lastEventId) : null)
                            ? { 'Last-Event-ID': (config.lastEventId ?? this.lastEventId) }
                            : {}),
                        ...this.config.headers,
                    },
                    body: JSON.stringify({
                        message,
                        model: config.model,
                        ...(config.messageId ? { messageId: config.messageId } : {}),
                        ...(attachments?.length ? { attachments } : {}),
                        ...(config.resume ? { resume: true } : {}),
                        // Per-send intent flags — the server tags the persisted user message
                        // so a suppressed driver message is hidden on read and an auto-sent
                        // message keeps its distinct style. Without forwarding these, a
                        // suppressed message would persist as an ordinary user message and
                        // reappear on refresh.
                        ...(config.suppressUserMessage ? { suppressUserMessage: true } : {}),
                        ...(config.automatic ? { automatic: true } : {}),
                        ...(config.userInitiated ? { userInitiated: true } : {}),
                        ...(config.viaDictation ? { viaDictation: true } : {}),
                    }),
                    signal,
                }, signal, Boolean(config.resume));
                if (response.status !== 409)
                    break;
                // Read the 409 body once. The lock-held answer carries no `errorKey`; a
                // 409 with one (for example `project.deleting`) is a final answer from the
                // server, shown at once instead of retried.
                conflictText = await response.text().catch(() => undefined);
                let hasErrorKey = false;
                try {
                    const parsed = conflictText ? JSON.parse(conflictText) : undefined;
                    hasErrorKey = typeof parsed?.errorKey === 'string' && parsed.errorKey.length > 0;
                }
                catch (_error) {
                    // Not JSON — treat as the legacy lock-held shape.
                }
                if (hasErrorKey)
                    break;
                // 409 Conflict — server lock still held. Wait with exponential backoff.
                if (attempt < HttpChatProvider.CONFLICT_MAX_RETRIES) {
                    const delay = HttpChatProvider.CONFLICT_BASE_DELAY * 2 ** attempt;
                    await new Promise((resolve) => {
                        const timer = setTimeout(resolve, delay);
                        // If aborted while waiting, resolve immediately so the AbortError
                        // is thrown on the next fetch attempt rather than hanging.
                        signal.addEventListener('abort', () => {
                            clearTimeout(timer);
                            resolve();
                        }, { once: true });
                    });
                    // If aborted during the delay, exit cleanly instead of retrying
                    if (signal.aborted)
                        return;
                }
            }
            if (!response.ok) {
                const text = (response.status === 409
                    ? conflictText
                    : await response.text().catch(() => undefined)) ??
                    t('chat.error.unknownError', undefined, { defaultValue: 'Unknown error' });
                // Parse structured limit error metadata from JSON responses. `limitType`
                // names the rule that fired; `billingAction` names the remedy the backend
                // resolved (add funds vs. add a payment method vs. upgrade vs. nothing to
                // sell) and `upgradeTier` the plan an upgrade would move to (`null` = no
                // higher tier). All three ride through to the client so the call-to-action
                // is the backend's answer, not a client-side default.
                let limitType;
                let requiresSignup;
                let billingAction;
                let upgradeTier;
                let errorMessage;
                try {
                    const body = JSON.parse(text);
                    if (typeof body.limitType === 'string')
                        limitType = body.limitType;
                    if (typeof body.requiresSignup === 'boolean')
                        requiresSignup = body.requiresSignup;
                    if (typeof body.billingAction === 'string')
                        billingAction = body.billingAction;
                    if (typeof body.upgradeTier === 'string' || body.upgradeTier === null)
                        upgradeTier = body.upgradeTier;
                    if (typeof body.error === 'string')
                        errorMessage = body.error;
                }
                catch (_error) {
                    // Not JSON — use raw text
                }
                onEvent({
                    type: 'error',
                    // Forward the HTTP status so the client can distinguish a retryable
                    // server error (5XX) from a client error (4XX) or a limit/quota gate.
                    status: response.status,
                    message: errorMessage ??
                        t('chat.error.httpError', { status: response.status, text }, { defaultValue: 'HTTP {{status}}: {{text}}' }),
                    limitType,
                    requiresSignup,
                    billingAction,
                    upgradeTier,
                });
                return;
            }
            if (!response.body) {
                onEvent({
                    type: 'error',
                    // Transport-layer failure (the HTTP response carried no stream) — the
                    // server never reported an error, so the turn is safely resumable.
                    transport: true,
                    message: t('chat.error.noResponseBody', undefined, { defaultValue: 'No response body' }),
                });
                return;
            }
            await HttpChatProvider.pumpSse(response.body, onEvent, (id) => {
                this.lastEventId = id;
            });
        }
        catch (err) {
            // AbortError can be a DOMException or a plain Error depending on the environment.
            // Silently return — this request was superseded by a newer one.
            if (err instanceof Error && err.name === 'AbortError') {
                return;
            }
            const message = err instanceof Error
                ? err.message
                : t('chat.error.streamError', undefined, { defaultValue: 'Stream error' });
            // Transport-layer failure: the fetch threw or the connection dropped
            // mid-stream (server restart/crash, network blip, proxy reset). The server
            // never sent an error event, so flag it `transport` — consumers auto-resume
            // the interrupted turn instead of surfacing a terminal error.
            onEvent({ type: 'error', transport: true, message });
        }
        finally {
            // Only clear if this call's controller is still the active one — a newer
            // sendMessage call may have already replaced it.
            if (this.abortController === controller) {
                this.abortController = null;
            }
        }
    }
    /**
     * Fetch, with a bounded retry ONLY while re-attaching to a running turn.
     *
     * A resume POST is an attach request: repeating it cannot start a second
     * turn (the server attaches to the one already running), so a 502/503/504
     * during a rolling restart is safe to wait out. A FRESH send is not retried
     * — it may create a turn, and there is no idempotency key to collapse a
     * duplicate.
     *
     * `Retry-After` is honored (whole seconds); otherwise exponential backoff
     * with jitter, capped at 5 s.
     *
     * @param url - The endpoint.
     * @param init - The request init (a string body is reusable across attempts).
     * @param signal - The caller's abort signal.
     * @param enabled - Whether retrying is allowed (a resume).
     * @returns The last response received.
     */
    static async fetchResumeAware(url, init, signal, enabled) {
        for (let attempt = 0;; attempt++) {
            const response = await fetch(url, init);
            const retryable = HttpChatProvider.RESUME_RETRY_STATUSES.includes(response.status);
            if (!enabled ||
                !retryable ||
                attempt >= HttpChatProvider.RESUME_MAX_RETRIES ||
                signal.aborted) {
                return response;
            }
            const retryAfter = response.headers.get('retry-after')?.trim();
            const fromHeader = retryAfter && /^\d+$/.test(retryAfter) ? Number(retryAfter) * 1_000 : undefined;
            const base = 300 * 2 ** attempt;
            const delay = Math.min(fromHeader ?? base + Math.random() * base, 5_000);
            await new Promise((resolve) => {
                const timer = setTimeout(resolve, delay);
                // An abort during the wait resolves immediately; the next check returns.
                signal.addEventListener('abort', () => {
                    clearTimeout(timer);
                    resolve();
                }, { once: true });
            });
        }
    }
    /**
     * Reads an SSE body and forwards each parsed `data:` event to the handler.
     * Shared by the main turn stream and the side-channel send.
     * @param body - The response body stream.
     * @param onEvent - Callback invoked per parsed event.
     * @param onId - Callback invoked with each SSE `id:` (the stream's resume cursor).
     */
    static async pumpSse(body, onEvent, onId) {
        const reader = body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (true) {
            const { done, value } = await reader.read();
            if (done)
                break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() ?? '';
            for (const line of lines) {
                // The SSE `id:` line is the resume cursor — remember the newest one so
                // a reconnect can ask the server to continue after it.
                if (line.startsWith('id: ')) {
                    onId?.(line.slice(4).trim());
                    continue;
                }
                if (!line.startsWith('data: '))
                    continue;
                const data = line.slice(6).trim();
                if (!data)
                    continue;
                try {
                    const event = JSON.parse(data);
                    onEvent(event);
                }
                catch (_error) {
                    // Skip malformed SSE data lines
                }
            }
            // Yield to the main thread between chunks so React can flush renders,
            // providing backpressure when a single read() returns many SSE events.
            await new Promise((r) => setTimeout(r, 0));
        }
        // Process any remaining buffer
        if (buffer.startsWith('data: ')) {
            const data = buffer.slice(6).trim();
            if (data) {
                try {
                    const event = JSON.parse(data);
                    onEvent(event);
                }
                catch (_error) {
                    // Skip malformed data
                }
            }
        }
    }
    /**
     * Sends a side-channel message (e.g. a human-only team note the server
     * intercepts before any agent turn) on its OWN request lifecycle. Never
     * touches `this.abortController`, so it is safe while `sendMessage` streams:
     * it cannot abort the live turn, and `abort()` cannot abort it. No 409 retry
     * loop — the server handles side-channel messages before taking the
     * per-conversation turn lock, so they never contend for it.
     * @param message - The side-channel message text (e.g. "/teamsay hi").
     * @param config - Chat configuration including the API endpoint.
     * @param onEvent - Callback invoked for each SSE event (typically one complete `message` + `done`).
     */
    async sendSideMessage(message, config, onEvent) {
        const url = `${this.config.baseUrl ?? ''}${config.endpoint}`;
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.config.headers,
                },
                body: JSON.stringify({
                    message,
                    ...(config.suppressUserMessage ? { suppressUserMessage: true } : {}),
                }),
            });
            if (!response.ok) {
                const text = await response
                    .text()
                    .catch(() => t('chat.error.unknownError', undefined, { defaultValue: 'Unknown error' }));
                let errorMessage;
                try {
                    const parsed = JSON.parse(text);
                    if (typeof parsed.error === 'string')
                        errorMessage = parsed.error;
                }
                catch (_error) {
                    // Not JSON — use raw text
                }
                onEvent({
                    type: 'error',
                    status: response.status,
                    message: errorMessage ??
                        t('chat.error.httpError', { status: response.status, text }, { defaultValue: 'HTTP {{status}}: {{text}}' }),
                });
                return;
            }
            if (!response.body) {
                onEvent({
                    type: 'error',
                    transport: true,
                    message: t('chat.error.noResponseBody', undefined, { defaultValue: 'No response body' }),
                });
                return;
            }
            await HttpChatProvider.pumpSse(response.body, onEvent);
        }
        catch (err) {
            const messageText = err instanceof Error
                ? err.message
                : t('chat.error.streamError', undefined, { defaultValue: 'Stream error' });
            onEvent({ type: 'error', transport: true, message: messageText });
        }
    }
    /** Sends an independent queue request without interrupting the live SSE stream. */
    async requestQueue(config, method, body) {
        const endpoint = `${this.config.baseUrl ?? ''}${config.endpoint}`;
        const queryAt = endpoint.indexOf('?');
        const url = queryAt < 0
            ? `${endpoint}-queue`
            : `${endpoint.slice(0, queryAt)}-queue${endpoint.slice(queryAt)}`;
        const response = await fetch(url, {
            method,
            headers: { 'Content-Type': 'application/json', ...this.config.headers },
            body: JSON.stringify(body),
            signal: AbortSignal.timeout(20_000),
        });
        const result = (await response.json());
        if (!response.ok) {
            throw new Error(result.error ??
                t('chat.error.sendFailed', undefined, {
                    defaultValue: 'Failed to send message',
                }));
        }
        return result;
    }
    /** Accept a message for the next safe tool boundary, preserving its stable id. */
    async queueMessage(message, config, onEvent, attachments, messageId) {
        const id = messageId ?? config.messageId ?? crypto.randomUUID();
        const result = await this.requestQueue(config, 'POST', {
            id,
            message,
            ...(attachments?.length ? { attachments } : {}),
            ...(config.suppressUserMessage ? { suppressUserMessage: true } : {}),
            ...(config.automatic ? { automatic: true } : {}),
            ...(config.userInitiated ? { userInitiated: true } : {}),
            ...(config.viaDictation ? { viaDictation: true } : {}),
        });
        if (result.sendImmediately) {
            // Reuse the independent command transport; it leaves the live SSE alone.
            let failed;
            await this.sendSideMessage(message, config, (event) => {
                if (event.type === 'error')
                    failed = event.message;
                onEvent(event);
            });
            if (failed)
                throw new Error(failed);
            onEvent({
                type: 'message',
                message: {
                    id,
                    role: 'user',
                    content: message,
                    timestamp: Date.now(),
                    queued: false,
                    hidden: true,
                },
            });
            return;
        }
        if (!result.message || result.message.id !== id || typeof result.message.queued !== 'boolean') {
            throw new Error(t('chat.error.sendFailed', undefined, { defaultValue: 'Failed to send message' }));
        }
        onEvent({ type: 'message', message: result.message });
    }
    /** Edit a pending message; a delivery race is reported instead of silently losing the edit. */
    async editQueuedMessage(messageId, message, config, attachments) {
        await this.requestQueue(config, 'PATCH', {
            id: messageId,
            message,
            ...(attachments ? { attachments } : {}),
        });
    }
    /** Cancel a message that has not yet been delivered. */
    async deleteQueuedMessage(messageId, config) {
        await this.requestQueue(config, 'DELETE', { id: messageId });
    }
    /** Aborts the current in-flight SSE stream, if any. */
    abort() {
        this.abortController?.abort();
        this.abortController = null;
    }
    /**
     * Sends an abort request to the server to kill the active AI stream.
     * Uses `navigator.sendBeacon` when available (works during page unload),
     * falls back to a fire-and-forget fetch POST.
     * @param config - Chat configuration with the endpoint URL.
     * @param conversationId - Optional conversation ID to target.
     * @param opts - Abort options. `userInitiated: true` marks an explicit user
     *   Stop (the Stop button): the server then records a durable stop marker that
     *   suppresses automatic follow-up turns until the user sends a new message.
     *   Leave unset for the page-unload beacon — a refresh is not a stop.
     */
    abortOnServer(config, conversationId, opts) {
        // Append -abort to the pathname only, preserving any query string.
        // e.g. "/chat?conversationId=x" → "/chat-abort?conversationId=x"
        const base = `${this.config.baseUrl ?? ''}${config.endpoint}`;
        const qIdx = base.indexOf('?');
        const url = qIdx === -1 ? `${base}-abort` : `${base.slice(0, qIdx)}-abort${base.slice(qIdx)}`;
        const body = JSON.stringify({
            ...(conversationId ? { conversationId } : {}),
            ...(opts?.userInitiated ? { userInitiated: true } : {}),
        });
        if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
            navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
        }
        else {
            fetch(url, {
                method: 'POST',
                headers: { ...this.config.headers, 'Content-Type': 'application/json' },
                body,
                keepalive: true,
            }).catch(() => { });
        }
    }
    /**
     * Clears chat history on the server by sending a DELETE request to the chat endpoint.
     * @param config - Chat configuration with the endpoint URL.
     */
    async clearHistory(config) {
        const url = `${this.config.baseUrl ?? ''}${config.endpoint}`;
        await fetch(url, {
            method: 'DELETE',
            headers: this.config.headers,
        });
    }
    /**
     * Loads chat history from the server via GET request.
     * @param config - Chat configuration with the endpoint URL.
     * @returns An array of chat messages. Rejects when history could not be read.
     */
    async loadHistory(config) {
        const url = `${this.config.baseUrl ?? ''}${config.endpoint}`;
        const response = await fetch(url, {
            headers: this.config.headers,
        });
        if (!response.ok)
            throw new Error(`Failed to load chat history: HTTP ${response.status}`);
        // Generic parse: `messages` + `streaming` are the only fields this shared
        // bond understands; every OTHER top-level field is app-specific and rides
        // through untouched in `lastMeta` (Anti-Pattern 14 — no baked-in `mode` or
        // any other consumer concept here).
        const { messages, streaming, ...meta } = (await response.json());
        this.isServerStreaming = streaming ?? false;
        this.lastMeta = meta;
        return (messages ?? []).map((m, i) => {
            // Backend stores timestamps as ISO strings; frontend needs numbers
            const raw = m.timestamp;
            const timestamp = typeof raw === 'number'
                ? raw
                : typeof raw === 'string'
                    ? new Date(raw).getTime()
                    : Date.now();
            const toolCalls = m.toolCalls?.map((tc) => ({
                ...tc,
                status: typeof tc.output === 'object' && tc.output !== null && 'error' in tc.output
                    ? 'error'
                    : 'done',
            }));
            // Reconstruct blocks from toolCalls when blocks aren't stored (legacy messages).
            // This ensures tool call cards render correctly from history.
            let blocks = m.blocks;
            if (!blocks && (m.content || toolCalls?.length)) {
                blocks = [];
                if (m.content)
                    blocks.push({ type: 'text', content: m.content });
                if (toolCalls) {
                    for (const tc of toolCalls)
                        blocks.push({ type: 'tool_call', id: tc.id });
                }
            }
            // Everything the server persisted on the message rides through VERBATIM, except
            // the three fields this bond has to reconstruct (`timestamp` ISO→number,
            // `toolCalls` status derivation, `blocks` legacy rebuild). That keeps every other
            // persisted field — the generic `cardEvent`/`attachments`/`aborted`/`author` AND any
            // app-specific message metadata a consumer stored (e.g. molecule.dev's `commitRecord`,
            // `loopLimitReached`, `automatic`) — round-tripping so a reloaded transcript matches
            // the live one, WITHOUT this shared bond naming any consumer's concept (Anti-Pattern
            // 14). Reconstructed keys are excluded from the spread so the raw values can't
            // override the derived ones.
            const { timestamp: _ts, toolCalls: _tc, blocks: _bl, ...persisted } = m;
            // Boundary assertion: `persisted` is untyped JSON (`Record<string, unknown>`) the
            // server round-tripped from `ChatMessage`; the explicit keys below re-establish the
            // typed shape. (Same deserialization boundary as the per-field `as` casts above.)
            return {
                ...persisted,
                id: m.id ?? `msg-${i}`,
                role: m.role,
                content: m.content ?? '',
                timestamp,
                blocks,
                toolCalls,
            };
        });
    }
}
/**
 * Creates an `HttpChatProvider` instance with optional base URL and custom headers.
 * @param config - HTTP-specific chat configuration (base URL, headers).
 * @returns An `HttpChatProvider` that communicates with the backend via HTTP/SSE.
 */
export function createProvider(config) {
    return new HttpChatProvider(config);
}
//# sourceMappingURL=provider.js.map